import { Rate } from 'interfaces/Rate.interface';
import { useEffect, useState } from 'preact/hooks';
import {
  fetchRateByShipmentID,
  generateDefaultRate,
  handleRatingForShipment,
} from 'services/rating.services';
import AppError from 'exceptions/AppError';
import { ptpCatchError } from 'services/sentry.services';
import { ErrorCodes } from 'constants/errors.constants';
import { isRuntimeError } from 'utils/fetcher.utils';
import { GlobalConfig } from '../../interfaces/GlobalConfig.interface';

export const useRating = (selectedParcel, globalConfig: GlobalConfig) => {
  const { id: trackingExpId, organizationId } = globalConfig;
  const { poId: shipmentId } = selectedParcel;
  const defaultRate = generateDefaultRate(
    shipmentId,
    trackingExpId,
    organizationId,
  );
  const [currentRate, setCurrentRate] = useState<Rate>(defaultRate);
  const [ratingError, setRatingError] = useState<string | undefined>(undefined);
  const [isRatingLoading, setIsRatingLoading] = useState<boolean>(false);
  const [isInitializedRating, setIsInitializedRating] =
    useState<boolean>(false);
  const [shownAdditionalRating, setShownAdditionalRating] =
    useState<boolean>(false);
  const closeAdditionalRating = () => {
    setShownAdditionalRating(false);
  };
  const openAdditionalRating = () => {
    setShownAdditionalRating(true);
  };

  useEffect(() => {
    setIsRatingLoading(true);
    setRatingError(undefined);
    const { poId: shipmentId } = selectedParcel;
    let rate: Rate = defaultRate;
    (async () => {
      try {
        rate = await fetchRateByShipmentID(shipmentId, defaultRate);
      } catch (error) {
        if (isRuntimeError(error)) {
          ptpCatchError(error);
          setRatingError(ErrorCodes.GeneralError);
        }
      } finally {
        setCurrentRate({
          ...rate,
          trackingExpId,
        });
        setIsRatingLoading(false);
        setIsInitializedRating(true);
      }
    })();
  }, [selectedParcel]);

  const rateForShipment = async (rateValue: number) => {
    if (isRatingLoading) {
      return;
    }
    setRatingError(undefined);
    setIsRatingLoading(true);
    try {
      const newRate: Rate = await handleRatingForShipment(
        rateValue,
        currentRate,
      );
      setCurrentRate(newRate);
      openAdditionalRating();
    } catch (error) {
      if (isRuntimeError(error)) {
        ptpCatchError(error);
      }
      setRatingError(error.message);
    } finally {
      setIsRatingLoading(false);
    }
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.has('pp_rating')) {
      queryParams.delete('pp_rating');
      window.history.pushState(
        null,
        '',
        `${window.location.origin}${
          window.location.pathname
        }?${queryParams.toString()}`,
      );
    }
  };

  return {
    isRatingLoading,
    currentRate,
    ratingError,
    rateForShipment,
    setCurrentRate,
    shownAdditionalRating,
    openAdditionalRating,
    closeAdditionalRating,
    isInitializedRating,
  };
};
